import { TypedDocumentNode, gql } from '@apollo/client';
import { faCheck, faXmark } from '@fortawesome/pro-solid-svg-icons';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import IconButton from '@sorare/core/src/atoms/buttons/IconButton';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Friendlies } from '@sorare/core/src/atoms/icons/Friendlies';
import { Horizontal, SBHorizontal } from '@sorare/core/src/atoms/layout/flex';
import { LinkOther } from '@sorare/core/src/atoms/navigation/Box';
import { LabelS } from '@sorare/core/src/atoms/typography';
import Avatar from '@sorare/core/src/components/user/Avatar';
import { glossary, rivals } from '@sorare/core/src/lib/glossary';

import { RivalryGameFooter_user } from './__generated__/index.graphql';

const Root = styled(SBHorizontal)`
  background-color: var(--c-neutral-300);
  padding: var(--intermediate-unit) var(--double-unit);
`;
const AvatarWrapper = styled(Horizontal).attrs({ center: true })`
  border-radius: 50%;
  border: 2px solid var(--c-neutral-400);
  overflow: hidden;

  & > img,
  & > svg {
    width: var(--double-unit);
    height: var(--double-unit);
    object-fit: cover;
  }
`;

type Props = {
  matchedUp: boolean;
  inviteReceived: boolean;
  inviteRejectedByMe: boolean;
  inviteRejectedByOpponent: boolean;
  user: RivalryGameFooter_user;
  onAcceptReceivedInvite: () => void;
  onRejectReceivedInvite: () => void;
};
export const RivalryGameFooter = ({
  matchedUp,
  inviteReceived,
  inviteRejectedByMe,
  inviteRejectedByOpponent,
  user,
  onAcceptReceivedInvite,
  onRejectReceivedInvite,
}: Props) => {
  const { formatMessage } = useIntl();

  if (matchedUp) {
    return (
      <Root>
        <Horizontal gap={1} center>
          <AvatarWrapper>
            <Friendlies />
          </AvatarWrapper>
          <LabelS as="span" bold>
            <FormattedMessage {...rivals.matchedUp} />
          </LabelS>
        </Horizontal>
      </Root>
    );
  }

  if (inviteRejectedByMe) {
    return (
      <Root>
        <Horizontal gap={1} center>
          <AvatarWrapper>
            <Avatar user={user} />
          </AvatarWrapper>
          <LabelS as="span" bold>
            <FormattedMessage {...rivals.youDeclined} />
          </LabelS>
        </Horizontal>
      </Root>
    );
  }

  if (inviteRejectedByOpponent) {
    return (
      <Root>
        <Horizontal gap={1} center>
          <AvatarWrapper>
            <Avatar user={user} />
          </AvatarWrapper>
          <LabelS as="span" bold>
            <FormattedMessage {...rivals.opponentDeclined} />
          </LabelS>
        </Horizontal>
      </Root>
    );
  }

  if (inviteReceived) {
    return (
      <Root>
        <Horizontal gap={1} center>
          <AvatarWrapper>
            <Avatar user={user} />
          </AvatarWrapper>
          <LabelS as="span" bold>
            <FormattedMessage {...rivals.challengedYou} />
          </LabelS>
        </Horizontal>
        <Horizontal gap={1} center>
          <LinkOther>
            <IconButton color="red" small onClick={onRejectReceivedInvite}>
              <FontAwesomeIcon
                icon={faXmark}
                size="sm"
                title={formatMessage(glossary.reject)}
              />
            </IconButton>
          </LinkOther>
          <LinkOther>
            <IconButton
              color="secondary"
              small
              onClick={onAcceptReceivedInvite}
            >
              <FontAwesomeIcon
                icon={faCheck}
                size="sm"
                title={formatMessage(glossary.accept)}
              />
            </IconButton>
          </LinkOther>
        </Horizontal>
      </Root>
    );
  }

  return (
    <Root>
      <Horizontal gap={1} center>
        <AvatarWrapper>
          <Avatar user={user} />
        </AvatarWrapper>
        <LabelS as="span" bold>
          <FormattedMessage {...rivals.youRequested} />
        </LabelS>
      </Horizontal>
    </Root>
  );
};

RivalryGameFooter.fragments = {
  user: gql`
    fragment RivalryGameFooter_user on User {
      slug
      ...Avatar_user
    }
    ${Avatar.fragments.user}
  ` as TypedDocumentNode<RivalryGameFooter_user>,
};
